import { SourceTypeResponse, SourceTypesQueryResponse } from "api/company";
import { useMemo } from "react";
import { useRouteMatch } from "react-router-dom";
import { getPrioritySourceType, sourceTypeHasData } from "utils";

import { routes } from "config/urls";

export interface RouteMatchResponse {
  isExact: boolean;
  params: {
    companyId: string;
    sourceType: string;
  };
  path: string;
  url: string;
}

export const useCurrentStep = (
  sourceType: SourceTypeResponse,
  currentSourceType: SourceTypeResponse | undefined,
  sourceTypes: SourceTypesQueryResponse
) => {
  const link: RouteMatchResponse | null = useRouteMatch(routes.company.link);
  const success: RouteMatchResponse | null = useRouteMatch(
    routes.company.success
  );
  const revisit: RouteMatchResponse | null = useRouteMatch(
    routes.company.revisit
  );
  const consent: RouteMatchResponse | null = useRouteMatch(
    routes.company.consent
  );
  const error: RouteMatchResponse | null = useRouteMatch(routes.company.error);
  const overview: RouteMatchResponse | null = useRouteMatch(
    routes.company.overview
  );
  const customIntro: RouteMatchResponse | null = useRouteMatch(
    routes.company.customIntro
  );
  const source = sourceType.sourceType.toLowerCase();

  const currentStep = useMemo(() => {
    const prioritySource =
      getPrioritySourceType(sourceTypes)?.sourceType.toLowerCase();
    const linkSourceType = link?.params.sourceType;

    if (
      (customIntro && customIntro.params.sourceType.toLowerCase() === source) ||
      (link && !linkSourceType && prioritySource === source) ||
      (link && linkSourceType && linkSourceType.toLowerCase() === source)
    ) {
      return 1;
    }

    if (
      (success && success.params.sourceType.toLowerCase() === source) ||
      (revisit && revisit.params.sourceType?.toLowerCase() === source)
    ) {
      return 4;
    }

    if (
      (consent && currentSourceType?.sourceType.toLowerCase() === source) ||
      (error && currentSourceType?.sourceType.toLowerCase() === source)
    ) {
      return 2;
    }

    return 0;
  }, [
    link,
    success,
    revisit,
    consent,
    error,
    source,
    currentSourceType,
    sourceTypes,
    customIntro,
  ]);

  const flowNotStarted = currentStep === 0;
  const hasNoData = !sourceTypeHasData(sourceType);

  const sourceTypeSkipped =
    (flowNotStarted &&
      currentSourceType?.priority &&
      currentSourceType?.priority > sourceType.priority &&
      hasNoData) ||
    (overview && hasNoData);

  const textDisabled =
    sourceTypeSkipped ||
    (!overview &&
      (!currentSourceType?.priority ||
        (flowNotStarted &&
          currentSourceType?.priority < sourceType.priority &&
          hasNoData)));

  return { currentStep, textDisabled, sourceTypeSkipped };
};
